import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { forwardRef, useCallback, useMemo, useRef } from 'react';

import { Attach, Remove } from '@webapp/common/assets/icons';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import { Loader } from '@webapp/common/components/loader';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { mapQuestionBlockPositionToAlignItems, setHtml } from '@webapp/common/lib/ui';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { QuestionState } from '@webapp/survey/src/machine/question-machine';
import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './file-upload.css';

const inputStyle = { display: 'none' };

const Upload = forwardRef<
    HTMLInputElement,
    {
        style: CSSProperties;
        loading?: boolean;
        onClick: () => void;
        buttonStyle: CSSProperties;
        uploadButtonName: string;
        onChange: ({ target: { files } }: { target: { files: any } }) => void;
        fileLimitExt: Array<string>;
    }
>(({ buttonStyle, fileLimitExt, loading, onChange, onClick, style, uploadButtonName }, ref) => (
    <div className={cn(css.upload, loading && css.hidden)}>
        <Loader className={css.loader} visible={loading} />
        <Attach style={style} />
        <div className={css.btn} style={buttonStyle} onClick={onClick}>
            {uploadButtonName}
        </div>
        <input
            multiple
            accept={fileLimitExt.join(',')}
            ref={ref}
            style={inputStyle}
            type='file'
            onChange={(v: any) => void onChange(v)}
        />
    </div>
));
Upload.displayName = 'Upload';

const Error: FC<{
    errorText: string;
}> = ({ errorText }) => <div className={cn(CssUiComponent.INVALID, css.error)} {...setHtml(errorText)} />;

const Answer: FC<{
    style: CSSProperties;
    onDelete: () => void;
    iconStyle: { color: string };
    name: string;
}> = ({ iconStyle, name, onDelete, style }) => (
    <div className={cn(CssPageBlock.ANSWER, css.file)} style={style}>
        <span className={CssUiComponent.LABEL}>{name}</span>
        <Remove className={css.deleteIcon} style={iconStyle} onClick={onDelete} />
    </div>
);

export const FileUpload: VFC<SurveyQuestion> = ({ actor, question }) => {
    const {
        design: {
            view: { questionBlockPosition },
        },
    } = useStateSelector(selectInfo);
    const {
        files,
        errorText,
        invalid,
        params: { fileAmount, fileLimitExt, uploadButtonName },
    } = question;

    const filesLoading = useSelector(
        actor,
        (q) => q.value === QuestionState.file_uploading || q.value === QuestionState.file_deleting,
    );
    const input = useRef<HTMLInputElement>(null);
    const { answerStyle, itemsGap, textStyle } = useTextStyle();
    const computedFontSize = parseInt(`${textStyle.fontSize}`) - 4;
    const fontSize = isNaN(computedFontSize) ? textStyle.fontSize : `${computedFontSize}px`;
    const style: CSSProperties = useMemo(
        () => ({
            ...textStyle,
            fontSize,
        }),
        [fontSize, textStyle],
    );
    const iconStyle = useMemo(() => ({ color: answerStyle.color, height: fontSize }), [answerStyle.color, fontSize]);

    const listStyle: CSSProperties = useMemo(
        () => ({
            alignItems: mapQuestionBlockPositionToAlignItems[questionBlockPosition],
            gap: itemsGap,
        }),
        [itemsGap, questionBlockPosition],
    );

    const handleClick = useCallback(() => {
        input.current?.click();
    }, []);

    const handleChooseFile = useCallback(
        ({ target: { files } }) => {
            actor.send({ type: 'VALIDATE_FILES', files });
        },
        [actor, invalid],
    );

    const handleDeleteFile = useCallback(
        (id: number) => () => {
            actor.send({ type: 'FILE_DELETE', id });
            if (input.current) {
                input.current.value = null;
            }
        },
        [actor],
    );

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.FILE_UPLOAD, css.files)} style={listStyle}>
            {errorText && <Error errorText={errorText} />}
            {files &&
                files.map(({ id, file: { name } }) => {
                    return (
                        <Answer
                            iconStyle={iconStyle}
                            key={id}
                            name={name}
                            style={style}
                            onDelete={handleDeleteFile(id)}
                        />
                    );
                })}
            {(!files || files.length < fileAmount) && (
                <Upload
                    buttonStyle={style}
                    fileLimitExt={fileLimitExt}
                    loading={filesLoading}
                    ref={input}
                    style={iconStyle}
                    uploadButtonName={uploadButtonName}
                    onChange={handleChooseFile}
                    onClick={handleClick}
                />
            )}
        </div>
    );
};
FileUpload.displayName = 'FileUpload';
